<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  // Cada entrada pode ser uma string ou { label, align: 'right', key } —
  // colunas numericas alinham o cabecalho junto com as celulas, e a `key`
  // (opcional) torna a coluna clicavel pra ordenar.
  headers: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  isEmpty: { type: Boolean, default: false },
  emptyMessage: { type: String, default: '' },
  sortKey: { type: String, default: '' },
  sortAsc: { type: Boolean, default: true },
  // Tabela com muitas colunas: aperta o respiro lateral pra caber sem cortar.
  dense: { type: Boolean, default: false },
});

const emit = defineEmits(['sort']);

const { t } = useI18n();

const headerLabel = header =>
  typeof header === 'object' && header !== null ? header.label : header;
const headerAlignRight = header =>
  typeof header === 'object' && header !== null && header.align === 'right';
const headerKey = header =>
  typeof header === 'object' && header !== null ? header.key : null;
</script>

<template>
  <div
    class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
  >
    <!-- Rola de lado em QUALQUER largura. Antes o desktop desligava o scroll
         (pra nao cortar popover absoluto de dentro da linha) e o preco era
         pior: coluna que nao coubesse era fatiada em silencio. Popover que
         precise escapar da tabela vai de Teleport + position fixed. -->
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead class="border-b border-n-weak bg-n-alpha-1">
          <tr>
            <th
              v-for="(header, index) in headers"
              :key="index"
              class="text-left text-[10.5px] font-bold uppercase tracking-[0.09em] text-n-slate-9 py-2.5 whitespace-nowrap"
              :class="{
                'px-3': dense,
                'px-5': !dense,
                'text-right':
                  headerAlignRight(header) || index === headers.length - 1,
                'cursor-pointer select-none hover:text-n-slate-11':
                  headerKey(header),
              }"
              @click="headerKey(header) && emit('sort', headerKey(header))"
            >
              {{ headerLabel(header) }}
              <span
                v-if="headerKey(header) && sortKey === headerKey(header)"
                class="ml-0.5 align-middle size-3 inline-block"
                :class="
                  sortAsc ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
                "
              />
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <slot v-if="!isLoading && !isEmpty" />
          <tr v-if="isLoading">
            <td :colspan="headers.length" class="px-5 py-12 text-center">
              <div
                class="flex items-center justify-center gap-2 text-n-slate-10"
              >
                <span class="i-lucide-loader-2 size-5 animate-spin" />
                <span class="text-sm">{{
                  t('GENERAL_SETTINGS.LOADING') || 'Loading...'
                }}</span>
              </div>
            </td>
          </tr>
          <tr v-else-if="isEmpty">
            <td :colspan="headers.length" class="px-5 py-12 text-center">
              <p class="text-sm text-n-slate-10">
                {{
                  emptyMessage ||
                  t('GENERAL_SETTINGS.NO_RECORDS') ||
                  'No records found'
                }}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
